import { Link } from 'react-router'

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-8">
          <Link to="/" className="text-primary-600 hover:underline">
            &larr; Back to Home
          </Link>
        </div>

        <div className="rounded-lg bg-white p-8 shadow">
          <h1 className="mb-6 text-3xl font-bold">Privacy Policy</h1>
          <p className="mb-4 text-sm text-gray-500">Last updated: 7 January 2025</p>

          <div className="prose prose-gray max-w-none">
            <h2 className="mt-8 text-xl font-semibold">1. Introduction</h2>
            <p className="mt-2 text-gray-600">
              Brightseed ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
            </p>
            <p className="mt-2 text-gray-600">
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
            </p>

            <h2 className="mt-8 text-xl font-semibold">2. Information We Collect</h2>

            <h3 className="mt-4 text-lg font-medium">Personal Information</h3>
            <p className="mt-2 text-gray-600">We collect information that you provide directly to us, including:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Account information (email address, password)</li>
              <li>Profile information (role as childminder or parent)</li>
              <li>Child information (name, date of birth, care notes)</li>
              <li>Daily updates (activities, meals, sleep, notes)</li>
              <li>Photos uploaded to the service</li>
              <li>Parent/guardian contact details</li>
            </ul>

            <h3 className="mt-4 text-lg font-medium">Automatically Collected Information</h3>
            <p className="mt-2 text-gray-600">When you use our app, we automatically collect:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Device information (device type, operating system)</li>
              <li>Push notification tokens (for sending notifications)</li>
              <li>Usage data (timestamps of actions)</li>
            </ul>

            <h3 className="mt-4 text-lg font-medium">Biometric Data</h3>
            <p className="mt-2 text-gray-600">
              If you enable biometric authentication (Face ID, Touch ID, or fingerprint), this data is processed entirely on your device by your operating system. We never receive, store, or have access to your biometric data.
            </p>

            <h2 className="mt-8 text-xl font-semibold">3. How We Use Your Information</h2>
            <p className="mt-2 text-gray-600">We use the information we collect to:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Provide, maintain, and improve our services</li>
              <li>Create and manage your account</li>
              <li>Enable childminders to share updates with parents</li>
              <li>Send push notifications about new updates</li>
              <li>Process subscription payments</li>
              <li>Respond to your requests and support inquiries</li>
              <li>Send transactional emails (verification, password reset)</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">4. Information Sharing</h2>
            <p className="mt-2 text-gray-600">
              We share your information only in the following circumstances:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Between Users:</strong> Childminders can share updates and photos with invited parents for specific children</li>
              <li><strong>Service Providers:</strong> We use third-party services that process data on our behalf (see Section 5)</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect rights and safety</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">5. Third-Party Services</h2>
            <p className="mt-2 text-gray-600">We use the following third-party services:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Firebase Cloud Messaging:</strong> For push notifications (Google)</li>
              <li><strong>Cloudflare R2:</strong> For secure image storage</li>
              <li><strong>Salable/Stripe:</strong> For payment processing</li>
              <li><strong>Resend:</strong> For transactional emails</li>
              <li><strong>PostHog:</strong> For analytics and error tracking (with your consent)</li>
            </ul>
            <p className="mt-2 text-gray-600">
              Each service has its own privacy policy governing the use of your information.
            </p>

            <h2 className="mt-8 text-xl font-semibold">5a. Mobile Platform Services</h2>
            <p className="mt-2 text-gray-600">
              Brightseed is available on the Apple App Store and Google Play Store. When you use our mobile app, the following platform-specific services and data collection apply:
            </p>

            <h3 className="mt-4 text-lg font-medium">Apple (iOS) Services</h3>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Apple Push Notification Service (APNs):</strong> We use Apple's push notification service to deliver notifications to your iOS device. Apple processes a device token to route notifications but does not have access to notification content.</li>
              <li><strong>App Store Analytics:</strong> Apple may collect anonymised app usage data if you have opted into sharing analytics with app developers. This is controlled by your iOS settings.</li>
              <li><strong>Crash Reports:</strong> If you have enabled crash data sharing in iOS settings, Apple may share crash reports with us to help improve app stability.</li>
              <li><strong>Face ID / Touch ID:</strong> Biometric data is processed entirely by iOS on your device. We only receive a success/failure response - we never access your biometric data.</li>
              <li><strong>App Tracking Transparency:</strong> We do not track you across other companies' apps or websites. We do not use the IDFA (Identifier for Advertisers).</li>
            </ul>
            <p className="mt-2 text-gray-600">
              For more information, see{' '}
              <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                Apple's Privacy Policy
              </a>.
            </p>

            <h3 className="mt-4 text-lg font-medium">Google (Android) Services</h3>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Firebase Cloud Messaging (FCM):</strong> We use Google's Firebase service to deliver push notifications to your Android device. Google processes a device token to route notifications.</li>
              <li><strong>Google Play Services:</strong> Our app uses Google Play Services for core functionality. Google may collect device and usage data as described in their privacy policy.</li>
              <li><strong>Play Store Analytics:</strong> Google may share anonymised app usage data with us through the Play Console if you have opted into sharing data with developers.</li>
              <li><strong>Crash Reports:</strong> If you have enabled crash data sharing in Android settings, Google may share crash reports with us via Android Vitals.</li>
              <li><strong>Advertising ID:</strong> We do not use the Google Advertising ID for tracking or advertising purposes.</li>
            </ul>
            <p className="mt-2 text-gray-600">
              For more information, see{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                Google's Privacy Policy
              </a>.
            </p>

            <h3 className="mt-4 text-lg font-medium">Device Permissions</h3>
            <p className="mt-2 text-gray-600">Our mobile app may request the following permissions:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Camera:</strong> To take photos for daily updates (only when you initiate)</li>
              <li><strong>Photo Library:</strong> To select existing photos for uploads</li>
              <li><strong>Notifications:</strong> To receive alerts about new updates and messages</li>
              <li><strong>Face ID / Touch ID / Fingerprint:</strong> For optional biometric app lock</li>
            </ul>
            <p className="mt-2 text-gray-600">
              All permissions are optional and can be managed in your device settings. The app will function without camera/photo access, but photo features will be unavailable.
            </p>

            <h2 id="cookies" className="mt-8 text-xl font-semibold">6. Cookies and Similar Technologies</h2>
            <p className="mt-2 text-gray-600">
              We use cookies and similar technologies to improve your experience. You can manage your cookie preferences at any time through our{' '}
              <Link to="/cookies" className="text-primary-600 hover:underline">Cookie Settings</Link>.
            </p>

            <h3 className="mt-4 text-lg font-medium">What Are Cookies?</h3>
            <p className="mt-2 text-gray-600">
              Cookies are small text files stored on your device when you visit a website or use an app. They help us remember your preferences and understand how you use our service.
            </p>

            <h3 className="mt-4 text-lg font-medium">Types of Cookies We Use</h3>

            <h4 className="mt-3 font-medium text-gray-700">Essential Cookies (Always Active)</h4>
            <p className="mt-1 text-gray-600">
              These cookies are necessary for the app to function and cannot be disabled. They include:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>brightseed-auth:</strong> Maintains your login session (7 days)</li>
              <li><strong>brightseed-cookie-consent:</strong> Stores your cookie preferences (1 year)</li>
              <li><strong>brightseed-app-lock:</strong> Stores app security settings (persistent)</li>
            </ul>

            <h4 className="mt-3 font-medium text-gray-700">Analytics Cookies (Optional)</h4>
            <p className="mt-1 text-gray-600">
              With your consent, we use analytics cookies to understand how you use Brightseed:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>PostHog (ph_*):</strong> Anonymous usage analytics, error tracking, and session replay (1 year)</li>
            </ul>

            <h4 className="mt-3 font-medium text-gray-700">Session Replay</h4>
            <p className="mt-1 text-gray-600">
              With your consent, we use session replay to record how you interact with our app. This helps us identify usability issues and improve the user experience. Session replays are recordings of your screen interactions (clicks, scrolls, navigation).
            </p>
            <p className="mt-2 text-gray-600">
              <strong>Privacy protections:</strong>
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>All text inputs (messages, notes, forms) are automatically masked and not recorded</li>
              <li>Child names are not captured in recordings</li>
              <li>Message content between parents and childminders is never recorded</li>
              <li>Passwords and sensitive fields are always excluded</li>
              <li>Session replay can be disabled at any time via cookie settings</li>
            </ul>
            <p className="mt-2 text-gray-600">
              <strong>Important:</strong> Analytics cookies never track the content of your messages, updates, or any personal information about children. They only track feature usage patterns (e.g., which pages are visited) and technical errors.
            </p>

            <h3 className="mt-4 text-lg font-medium">Your Cookie Choices</h3>
            <p className="mt-2 text-gray-600">
              You have the right to:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Accept or reject optional cookies when first visiting our site</li>
              <li>Change your cookie preferences at any time via{' '}
                <Link to="/cookies" className="text-primary-600 hover:underline">Cookie Settings</Link>
              </li>
              <li>Block cookies through your browser settings (note: this may affect functionality)</li>
            </ul>

            <h3 className="mt-4 text-lg font-medium">Do Not Track</h3>
            <p className="mt-2 text-gray-600">
              We respect the Do Not Track (DNT) browser setting. If you have DNT enabled, we will not load analytics cookies even if you have previously consented.
            </p>

            <h2 className="mt-8 text-xl font-semibold">7. Data Retention</h2>
            <p className="mt-2 text-gray-600">
              We retain your personal information for as long as your account is active or as needed to provide services. You can request deletion of your account and associated data at any time.
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Account data: Retained until account deletion</li>
              <li>Child profiles and updates: Retained until deleted by user or account deletion</li>
              <li>Images: Stored securely and deleted when the associated update is deleted</li>
              <li>Email verification tokens: Automatically deleted after 24 hours</li>
              <li>Session tokens: Automatically deleted after 7 days</li>
              <li>Analytics data: Retained for up to 12 months, then anonymised</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">8. Data Security</h2>
            <p className="mt-2 text-gray-600">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>All data transmitted over HTTPS encryption</li>
              <li>Passwords are securely hashed (never stored in plain text)</li>
              <li>Messages are encrypted at rest using AES-256-GCM</li>
              <li>Images are stored with signed URLs (time-limited access)</li>
              <li>API rate limiting to prevent abuse</li>
              <li>Optional biometric and PIN authentication</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">9. Your Rights Under GDPR</h2>
            <p className="mt-2 text-gray-600">
              Under the General Data Protection Regulation (GDPR) and UK Data Protection Act 2018, you have the following rights:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your account and data ("right to be forgotten")</li>
              <li><strong>Right to Data Portability:</strong> Request your data in a portable format (JSON export available in Settings)</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time (e.g., for analytics cookies)</li>
            </ul>
            <p className="mt-2 text-gray-600">
              To exercise these rights, please contact us at support@brightseed.app or use the relevant features in Settings (data export, account deletion).
            </p>

            <h2 className="mt-8 text-xl font-semibold">10. Legal Basis for Processing</h2>
            <p className="mt-2 text-gray-600">
              We process your personal data on the following legal bases:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li><strong>Contract:</strong> Processing necessary to provide our services (account management, updates, messaging)</li>
              <li><strong>Consent:</strong> Analytics cookies and marketing communications (you can withdraw consent at any time)</li>
              <li><strong>Legitimate Interests:</strong> Security measures, fraud prevention, and service improvement</li>
              <li><strong>Legal Obligation:</strong> Compliance with applicable laws and regulations</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">11. Children's Privacy</h2>
            <p className="mt-2 text-gray-600">
              Our service is designed for use by adult childminders and parents. Children do not create accounts or directly use the service. Information about children (names, photos, updates) is provided and managed by their parents/guardians and childminders.
            </p>

            <h2 className="mt-8 text-xl font-semibold">12. International Data Transfers</h2>
            <p className="mt-2 text-gray-600">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with GDPR:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Standard Contractual Clauses (SCCs) with service providers</li>
              <li>Adequacy decisions where applicable</li>
              <li>Data processing agreements with all third-party processors</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">13. Changes to This Policy</h2>
            <p className="mt-2 text-gray-600">
              We may update this privacy policy from time to time. We will notify you of any material changes by posting the new privacy policy on this page and updating the "Last updated" date. For significant changes, we may also notify you via email.
            </p>

            <h2 className="mt-8 text-xl font-semibold">14. Contact Us</h2>
            <p className="mt-2 text-gray-600">
              If you have questions about this Privacy Policy or wish to exercise your data protection rights, please contact us at:
            </p>
            <p className="mt-2 text-gray-600">
              Email: support@brightseed.app
            </p>
            <p className="mt-2 text-gray-600">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data protection rights have been violated:{' '}
              <a
                href="https://ico.org.uk/make-a-complaint/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                ico.org.uk/make-a-complaint
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
